// eslint-disable-next-line no-unused-vars
/* global sprypointCarouselConfig */

/**
 * Frontend script for SP Carousel Block.
 */

document.addEventListener( 'DOMContentLoaded', () => {
	const carousels = document.querySelectorAll(
		'.wp-block-sprypoint-carousel'
	);

	carousels.forEach( ( carousel ) => {
		const track = carousel.querySelector( '.sp-carousel-track' );

		// Attempt immediate initialization if slides are present
		if ( track && track.children.length > 0 ) {
			new SprypointCarousel( carousel );
		} else if ( track ) {
			// Slides not found yet, observe the track for additions
			const observer = new MutationObserver( ( mutationsList, obs ) => {
				// Check if nodes were added and the track now has children
				for ( const mutation of mutationsList ) {
					if (
						mutation.type === 'childList' &&
						track.children.length > 0
					) {
						new SprypointCarousel( carousel );
						obs.disconnect(); // Stop observing once initialized
						return; // Exit after successful initialization
					}
				}
			} );

			// Start observing the track element for child list changes
			observer.observe( track, { childList: true } );
		} else {
			// Track itself wasn't found, log an error (shouldn't happen with valid block markup)
			console.error( 'Carousel track element not found.', carousel );
		}
	} );
} );

// Debounce utility function
function debounce( func, wait ) {
	let timeout;
	return function executedFunction( ...args ) {
		const later = () => {
			clearTimeout( timeout );
			func.apply( this, args );
		};
		clearTimeout( timeout );
		timeout = setTimeout( later, wait );
	};
}

class SprypointCarousel {
	/**
	 * Constructor.
	 *
	 * @param {HTMLElement} element The block element.
	 */
	constructor( element ) {
		this.element = element;
		this.trackContainer = this.element.querySelector(
			'.sp-carousel-track-container'
		);
		this.track = this.element.querySelector( '.sp-carousel-track' );
		this.slides = Array.from( this.track?.children || [] );
		this.controlsWrapper = this.element.querySelector(
			'.sp-carousel-controls-wrapper'
		);
		this.dotsContainer = this.element.querySelector( '.sp-carousel-dots' );
		this.prevArrow = this.element.querySelector(
			'.sp-carousel-arrow--prev'
		);
		this.nextArrow = this.element.querySelector(
			'.sp-carousel-arrow--next'
		);

		if ( ! this.track || this.slides.length === 0 ) {
			console.error(
				'Carousel track or slides not found.',
				this.element
			);
			return; // Don't initialize if structure is invalid.
		}

		this.config = this.readConfig();
		this.currentIndex = 0;
		this.itemsPerView = 1; // Will be calculated dynamically.
		this.slideWidth = 0; // Will be calculated dynamically.
		this.gap = 0; // Will be calculated dynamically.
		this.totalPages = 0; // Will be calculated dynamically.
		this.isReducedMotion = window.matchMedia(
			'(prefers-reduced-motion: reduce)'
		).matches;
		this.autoplayTimer = null;
		this.isPaused = false;

		this.init();
	}

	/**
	 * Read configuration from data attributes.
	 *
	 * @return {Object} Configuration object.
	 */
	readConfig() {
		const dataset = this.element.dataset;
		return {
			showArrows: dataset.showArrows === 'true',
			showDots: dataset.showDots === 'true',
			loop: dataset.loop === 'true',
			autoplay: dataset.autoplay === 'true',
			autoplayDelay: parseInt( dataset.autoplayDelay || '5000', 10 ),
			transitionSpeed: parseInt( dataset.transitionSpeed || '300', 10 ),
		};
	}

	/**
	 * Initialize the carousel.
	 */
	init() {
		// 1. Calculate initial sizes and items per view.
		this.updateLayout();

		// 2. Set initial CSS transition speed.
		this.track.style.transitionDuration = this.isReducedMotion
			? '0ms'
			: `${ this.config.transitionSpeed }ms`;

		// 3. Generate pagination dots if enabled.
		if ( this.config.showDots ) {
			this.generateDots();
		}

		// 4. Bind event listeners.
		this.bindEvents();

		// 5. Set initial state for controls.
		this.updateControlsState();

		// 6. Start autoplay if enabled.
		if ( this.config.autoplay && ! this.isReducedMotion ) {
			this.startAutoplay();
		}

		// 7. Set initial ARIA attributes (basic setup).
		this.updateAriaStates();

		// 8. Add class to signal JS initialization is complete for CSS styling
		this.element.classList.add( 'is-js-initialized' );
	}

	/**
	 * Bind all necessary event listeners.
	 */
	bindEvents() {
		if ( this.config.showArrows ) {
			this.prevArrow?.addEventListener( 'click', () => this.prevSlide() );
			this.nextArrow?.addEventListener( 'click', () => this.nextSlide() );
		}

		// Dots event listeners are added in generateDots

		// Autoplay pause/resume listeners
		if ( this.config.autoplay ) {
			this.element.addEventListener( 'mouseenter', () =>
				this.pauseAutoplay()
			);
			this.element.addEventListener( 'mouseleave', () =>
				this.resumeAutoplay()
			);
			this.element.addEventListener( 'focusin', () =>
				this.pauseAutoplay()
			);
			this.element.addEventListener( 'focusout', () =>
				this.resumeAutoplay()
			);
		}

		// Resize listener - Debounced
		window.addEventListener(
			'resize',
			debounce( this.handleResize.bind( this ), 250 ) // Debounce resize events (250ms)
		);
	}

	/**
	 * Calculate layout parameters based on current dimensions.
	 */
	updateLayout() {
		if ( this.slides.length === 0 ) {
			return;
		}

		// Reset temporary styles if any were applied previously
		// (e.g., for measurement purposes, though not strictly needed here yet)

		const containerWidth = this.trackContainer.offsetWidth;
		const trackStyle = window.getComputedStyle( this.track );
		this.gap = parseFloat( trackStyle.gap ) || 0;

		// --- Dynamic Items Per View Logic ---
		const slide1Width = this.slides[ 0 ].offsetWidth;
		let canShowTwo = false;

		if ( this.slides.length > 1 ) {
			const slide2Width = this.slides[ 1 ].offsetWidth;
			const combinedWidth = slide1Width + slide2Width + this.gap;
			if ( combinedWidth <= containerWidth ) {
				canShowTwo = true;
			}
		}

		this.itemsPerView = canShowTwo ? 2 : 1;

		// We don't need to set slide widths explicitly here.
		// CSS flex-basis or width (e.g., width: 100% or width: calc(50% - gap/2)) should handle it.
		// The itemsPerView is mainly for calculating pagination and offsets.

		// Recalculate total pages based on the dynamic itemsPerView
		this.totalPages = Math.ceil( this.slides.length / this.itemsPerView );

		// Adjust current index if it becomes invalid after resize/layout change
		// Ensure index is never negative and not beyond the last valid page
		const oldIndex = this.currentIndex;
		this.currentIndex = Math.max(
			0,
			Math.min( this.currentIndex, this.totalPages - 1 )
		);

		// Apply transform based on the (potentially new) index and layout
		// We call goToSlide only if the index actually needs changing OR if it's the initial call
		// This avoids unnecessary transforms during init if index 0 is already correct.
		// However, the offset calculation *within* goToSlide depends on itemsPerView,
		// so we *must* call it to apply the potentially new offset even if index is unchanged.
		this.goToSlide( this.currentIndex, false ); // Go without animation
	}

	/**
	 * Generate pagination dots.
	 */
	generateDots() {
		if ( ! this.dotsContainer ) {
			return;
		}

		this.dotsContainer.innerHTML = ''; // Clear existing dots
		for ( let i = 0; i < this.totalPages; i++ ) {
			const dot = document.createElement( 'button' );
			dot.setAttribute( 'role', 'tab' );
			dot.setAttribute(
				'aria-label',
				`Go to slide ${ i * this.itemsPerView + 1 }`
			);
			dot.setAttribute( 'aria-controls', this.element.id );
			dot.addEventListener( 'click', () => this.goToSlide( i ) );
			this.dotsContainer.appendChild( dot );
		}
		this.updateDotsState();
	}

	/**
	 * Navigate to the previous slide/page.
	 */
	prevSlide() {
		let newIndex = this.currentIndex - 1;
		if ( newIndex < 0 ) {
			newIndex = this.config.loop ? this.totalPages - 1 : 0;
		}
		this.goToSlide( newIndex );
	}

	/**
	 * Navigate to the next slide/page.
	 */
	nextSlide() {
		let newIndex = this.currentIndex + 1;
		if ( newIndex >= this.totalPages ) {
			newIndex = this.config.loop ? 0 : this.totalPages - 1;
		}
		this.goToSlide( newIndex );
	}

	/**
	 * Go to a specific slide index.
	 *
	 * @param {number}  index   Index of the page to navigate to (0-based).
	 * @param {boolean} animate Animate the transition.
	 */
	goToSlide( index, animate = true ) {
		if (
			index < 0 ||
			index >= this.totalPages ||
			this.slides.length === 0
		) {
			return;
		}

		this.currentIndex = index;

		// Calculate transform offset
		// We need the starting slide index for the target page.
		const startSlideIndexOfPage = index * this.itemsPerView;
		let offset = 0;

		// Sum the widths and gaps of all slides *before* the target page starts.
		for ( let i = 0; i < startSlideIndexOfPage; i++ ) {
			if ( this.slides[ i ] ) {
				offset -= this.slides[ i ].offsetWidth + this.gap;
			}
		}
		// If the last slide on the previous page didn't have a gap applied (e.g., gap is on the right),
		// we might need to adjust slightly, but typically summing widths + gaps works.

		// Apply transform
		this.track.style.transform = `translateX(${ offset }px)`;

		// Update controls and ARIA states
		this.updateControlsState();
		this.updateAriaStates();

		// Restart autoplay if manually navigated and animation occurred
		if ( this.config.autoplay && animate && ! this.isReducedMotion ) {
			this.resetAutoplay();
		}
	}

	/**
	 * Update the visual state of dots and arrows.
	 */
	updateControlsState() {
		this.updateDotsState();
		this.updateArrowsState();
	}

	/**
	 * Update the state of pagination dots.
	 */
	updateDotsState() {
		if ( ! this.config.showDots || ! this.dotsContainer ) {
			return;
		}
		const dots = this.dotsContainer.querySelectorAll( 'button' );
		dots.forEach( ( dot, i ) => {
			if ( i === this.currentIndex ) {
				dot.setAttribute( 'aria-selected', 'true' );
				// TODO: Add active class for styling
				dot.classList.add( 'is-active' );
			} else {
				dot.setAttribute( 'aria-selected', 'false' );
				dot.classList.remove( 'is-active' );
			}
		} );
	}

	/**
	 * Update the state (enabled/disabled) of arrow buttons.
	 */
	updateArrowsState() {
		if ( ! this.config.showArrows || this.config.loop ) {
			// Arrows are always enabled if looping
			if ( this.prevArrow ) {
				this.prevArrow.disabled = false;
			}
			if ( this.nextArrow ) {
				this.nextArrow.disabled = false;
			}
			return;
		}

		if ( this.prevArrow ) {
			this.prevArrow.disabled = this.currentIndex === 0;
		}
		if ( this.nextArrow ) {
			this.nextArrow.disabled = this.currentIndex === this.totalPages - 1;
		}
	}

	/**
	 * Handle window resize events.
	 */
	handleResize() {
		// This call is now debounced via bindEvents.
		const previousItemsPerView = this.itemsPerView;
		this.updateLayout();

		// Regenerate dots if the number of pages changed.
		if (
			this.config.showDots &&
			previousItemsPerView !== this.itemsPerView
		) {
			this.generateDots();
		}
		this.updateControlsState(); // Re-check arrow states
	}

	/**
	 * Start the autoplay timer.
	 */
	startAutoplay() {
		if ( this.autoplayTimer ) {
			clearInterval( this.autoplayTimer );
		}
		this.isPaused = false;
		this.autoplayTimer = setInterval( () => {
			if ( ! this.isPaused ) {
				this.nextSlide();
			}
		}, this.config.autoplayDelay );
	}

	/**
	 * Stop the autoplay timer permanently.
	 */
	stopAutoplay() {
		if ( this.autoplayTimer ) {
			clearInterval( this.autoplayTimer );
			this.autoplayTimer = null;
		}
	}

	/**
	 * Pause autoplay (e.g., on hover/focus).
	 */
	pauseAutoplay() {
		if ( this.config.autoplay && this.autoplayTimer ) {
			this.isPaused = true;
		}
	}

	/**
	 * Resume autoplay if it was paused.
	 */
	resumeAutoplay() {
		if ( this.config.autoplay && this.autoplayTimer ) {
			this.isPaused = false;
		}
	}

	/**
	 * Reset autoplay timer (e.g., after manual interaction).
	 */
	resetAutoplay() {
		if ( this.config.autoplay && ! this.isReducedMotion ) {
			this.stopAutoplay();
			this.startAutoplay();
		}
	}

	/**
	 * Update necessary ARIA attributes based on state.
	 */
	updateAriaStates() {
		// Update dot states (handles aria-selected)
		this.updateDotsState();

		// Update slide visibility (aria-hidden)
		const firstVisibleSlideIndex = this.currentIndex * this.itemsPerView;
		const lastVisibleSlideIndex =
			firstVisibleSlideIndex + this.itemsPerView - 1;

		this.slides.forEach( ( slide, i ) => {
			if ( i >= firstVisibleSlideIndex && i <= lastVisibleSlideIndex ) {
				slide.removeAttribute( 'aria-hidden' );
				// Ensure focusable elements within are reachable if needed
				// (Though direct focus might be better managed via controls)
			} else {
				slide.setAttribute( 'aria-hidden', 'true' );
				// Optional: Remove focus from elements within hidden slides
				slide
					.querySelectorAll(
						'a, button, input, textarea, select, [tabindex]'
					)
					.forEach( ( el ) => el.setAttribute( 'tabindex', '-1' ) );
			}
		} );

		// Ensure controls have correct aria-controls (already set in render.php)
		// Ensure track container has aria-live="polite" (already set in render.php)
	}
}
